import { styled } from '@mui/material/styles';
import { ErrorComponent, type ErrorProps } from '@openx/components-core/lib/Error/Error.tsx';
import { NotFoundImage } from '@openx/components-core/lib/Error/pictures/NotFound.tsx';
import { ErrorBoundary } from '@openx/components-core/lib/ErrorBoundary/ErrorBoundary.tsx';
import { Loader } from '@openx/components-core/lib/Loader/Loader.tsx';
import { Logo } from '@openx/components-core/lib/Logo/Logo.tsx';
import { TOP_BAR_HEIGHT } from '@openx/constants/styles.ts';
import i18n from 'i18next';
import { type JSX, useEffect } from 'react';
import { useContractFetch } from '../../api/hooks/useContractFetch.ts';
import { sentGoogleResult, useGoogleIntegration } from '../../modules/googleIntegration/googleIntegration.ts';
import { Result } from '../../modules/googleIntegration/types.ts';
import { LandingPage } from '../../modules/landingPage/LandingPage.tsx';
import { CtaProvider } from '../../state/ctaProvider/CtaProvider.tsx';

import { LanguagePicker } from './LanguagePicker.tsx';

const StyledLogoContainerDiv = styled('div')`
  display: flex;
  width: 170px;
`;

const StyledContainerHeader = styled('header')`
  align-items: center;
  background-color: ${({ theme }) => theme.palette.background.paper};
  border-bottom: 1px solid ${({ theme }) => theme.palette.divider};
  display: flex;
  height: ${TOP_BAR_HEIGHT}px;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing(0, 4)};
  z-index: 2;
  flex-shrink: 0;
`;

const StyledContentWrapper = styled('div')`
  overflow: auto;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;

export function Dashboard(): JSX.Element {
  const { error, pending, data } = useGoogleIntegration();
  const pubId = data?.['publisher-id'];

  const { isContractLoading, contractError, contract, fetchContract } = useContractFetch();

  useEffect(() => {
    pubId && fetchContract(pubId);
  }, [fetchContract, pubId]);

  const errorProps: ErrorProps = {
    PictureComponent: NotFoundImage,
    actions: [
      {
        children: i18n.t('Back to google'),
        color: 'secondary',
        'data-test': 'button-back-to-google',
        onClick: () => {
          sentGoogleResult(Result.ERROR);
        },
        variant: 'contained',
      },
    ],
    subtitle: i18n.t("We aren't able to process your request. Please try again"),
    title: i18n.t('Something went wrong'),
  };

  if (error || contractError) {
    return (
      <ErrorComponent
        title={i18n.t('Something went wrong')}
        subtitle={i18n.t("We aren't able to process your request. Please try again")}
        PictureComponent={NotFoundImage}
        data-test="error-on-google-integration"
      />
    );
  }

  if (pending || isContractLoading) return <Loader />;

  return (
    <ErrorBoundary errorProps={errorProps}>
      <CtaProvider googleData={data} alreadySignedPublisher={contract}>
        <StyledContainerHeader>
          <StyledLogoContainerDiv>
            <Logo data-test="logo" />
          </StyledLogoContainerDiv>
          <LanguagePicker />
        </StyledContainerHeader>
        <StyledContentWrapper>
          <LandingPage />
        </StyledContentWrapper>
      </CtaProvider>
    </ErrorBoundary>
  );
}
